import { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { Pencil } from 'lucide-react';

import { PERM } from '#/config/permissions';
import { useCan } from '#/modules/infra/auth/useCan';
import { ROUTES } from '#/config/routes';
import { approveStockAdjustment } from '#/api/imsMgmt/receipts/stockAdjustment/approve';
import { cancelStockAdjustment } from '#/api/imsMgmt/receipts/stockAdjustment/cancel';
import { rejectStockAdjustment } from '#/api/imsMgmt/receipts/stockAdjustment/reject';
import { submitStockAdjustment } from '#/api/imsMgmt/receipts/stockAdjustment/submit';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import DocumentRejectReasonDialog from '#/modules/imsMgmt/receipts/shared/DocumentRejectReasonDialog';
import { DESTRUCTIVE_SOLID_BUTTON_CLASS } from '#/modules/imsMgmt/receipts/shared/destructiveSolidButtonClass';
import {
  getStockAdjustmentApiErrorMessage,
  isStockAdjustmentVersionConflict,
} from '#/modules/imsMgmt/receipts/stockAdjustment/helper/apiError';
import {
  canApproveStockAdjustment,
  canCancelStockAdjustment,
  canEditStockAdjustment,
  canEditStockAdjustmentRemark,
  canRejectStockAdjustment,
  canSubmitStockAdjustment,
} from '#/modules/imsMgmt/receipts/stockAdjustment/helper/adjustmentStatus';
import { toast } from '#/utils/toast';
import {
  stockAdjustmentDetailQueryKey,
  stockAdjustmentListQueryKeyPrefix,
} from '#/utils/queryKeys';

const StockAdjustmentCommandBar = ({ detail, onRefetch }) => {
  const canCreate = useCan(PERM.STOCK_ADJUSTMENT_CREATE);
  const canUpdate = useCan(PERM.STOCK_ADJUSTMENT_UPDATE);
  const canReview = useCan(PERM.STOCK_ADJUSTMENT_REVIEW);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);

  const id = detail?.id;
  const updatedAt = detail?.record?.updatedAt ?? detail?.updatedAt;
  const status = detail?.status;

  async function invalidateAll() {
    await queryClient.invalidateQueries({ queryKey: stockAdjustmentListQueryKeyPrefix() });
    if (id) {
      await queryClient.invalidateQueries({ queryKey: stockAdjustmentDetailQueryKey(id) });
    }
    await onRefetch?.();
  }

  const commandMutation = useMutation({
    mutationFn: async ({ action, payload }) => {
      if (action === 'submit') {
        return submitStockAdjustment(id, payload);
      }
      if (action === 'approve') {
        return approveStockAdjustment(id, payload);
      }
      if (action === 'reject') {
        return rejectStockAdjustment(id, payload);
      }
      if (action === 'cancel') {
        return cancelStockAdjustment(id);
      }
      throw new Error(`Unknown action: ${action}`);
    },
    onSuccess: async (result, variables) => {
      toast.success(result?.message || '操作成功');
      if (variables.action === 'reject') {
        setRejectDialogOpen(false);
      }
      if (variables.action === 'cancel') {
        await queryClient.invalidateQueries({ queryKey: stockAdjustmentListQueryKeyPrefix() });
        navigate(ROUTES.stockAdjustment);
        return;
      }
      await invalidateAll();
    },
    onError: (error) => {
      toast.error(getStockAdjustmentApiErrorMessage(error));
      if (isStockAdjustmentVersionConflict(error)) {
        onRefetch?.();
      }
    },
  });

  const isPending = commandMutation.isPending;

  async function handleSubmit() {
    const ok = await confirm({
      title: '提交調整單',
      message: '提交後將鎖定相關庫存並進入待核准，確定提交？',
      confirmText: '提交',
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'submit', payload: { updatedAt } });
  }

  async function handleApprove() {
    const ok = await confirm({
      title: '核准調整單',
      message: '核准後將更新庫存數量，確定核准？',
      confirmText: '核准',
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'approve', payload: { updatedAt } });
  }

  function handleRejectConfirm(rejectReason) {
    commandMutation.mutate({
      action: 'reject',
      payload: { updatedAt, rejectReason },
    });
  }

  async function handleCancel() {
    const ok = await confirm({
      title: '刪除草稿',
      message: '刪除後無法復原，確定刪除？',
      confirmText: '刪除',
      destructive: true,
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'cancel' });
  }

  return (
    <>
      {ConfirmDialogHost}
      <DocumentRejectReasonDialog
        open={rejectDialogOpen}
        onOpenChange={setRejectDialogOpen}
        title="退回調整單"
        description="退回後將恢復為草稿並釋放鎖定，確定退回？"
        confirmText="退回"
        isPending={isPending}
        onConfirm={handleRejectConfirm}
      />
      {canEditStockAdjustment(status) && canUpdate ? (
        <Button asChild variant="outline">
          <Link to={ROUTES.stockAdjustmentEdit(id)}>編輯草稿</Link>
        </Button>
      ) : null}
      {canEditStockAdjustmentRemark(status) && canUpdate ? (
        <Button asChild variant="secondary">
          <Link to={ROUTES.stockAdjustmentRemark(id)}>
            <Pencil className="size-3.5" aria-hidden />
            編輯備註
          </Link>
        </Button>
      ) : null}
      {canSubmitStockAdjustment(status) && canCreate ? (
        <Button disabled={isPending} onClick={handleSubmit}>
          提交
        </Button>
      ) : null}
      {canApproveStockAdjustment(status) && canReview ? (
        <Button disabled={isPending} onClick={handleApprove}>
          核准
        </Button>
      ) : null}
      {canRejectStockAdjustment(status) && canReview ? (
        <Button
          variant="destructive"
          className={cn(DESTRUCTIVE_SOLID_BUTTON_CLASS)}
          disabled={isPending}
          onClick={() => setRejectDialogOpen(true)}
        >
          退回
        </Button>
      ) : null}
      {canCancelStockAdjustment(status) && canCreate ? (
        <Button
          variant="destructive"
          className={cn(DESTRUCTIVE_SOLID_BUTTON_CLASS)}
          disabled={isPending}
          onClick={handleCancel}
        >
          刪除草稿
        </Button>
      ) : null}
    </>
  );
};

StockAdjustmentCommandBar.propTypes = {
  detail: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    updatedAt: PropTypes.string,
    record: PropTypes.shape({
      updatedAt: PropTypes.string,
    }),
  }).isRequired,
  onRefetch: PropTypes.func,
};

export default StockAdjustmentCommandBar;
